import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalSizes,
  createTheme,
} from 'flowbite-react';
import { DynamicStringEnumKeysOf } from 'flowbite-react/types';
import React, { createContext, useCallback, useState } from 'react';
import { HiX } from 'react-icons/hi';

interface ModalConfig {
  content: React.ReactNode;
  title?: string;
  onClose?: () => void;
  opaqueBackground?: boolean;
  modalClassName?: string;
  containerWidth?: number;
  size?: DynamicStringEnumKeysOf<ModalSizes> | 'auto';
  modalType?: 'simple' | 'confirm' | 'custom';
}

export interface ConfirmModalConfig {
  title: string;
  content: React.ReactNode;
  okBtn?: string;
  cancelBtn?: string;
  opaqueBackground?: boolean;
  modalClassName?: string;
  onConfirmCb?: () => void;
}

export interface ModalContextType {
  openModal: (config: ModalConfig) => void;
  openConfirmModal: (config: ConfirmModalConfig) => Promise<boolean>;
  closeModal: () => void;
}

export const ModalContext = createContext<ModalContextType | undefined>(
  undefined
);

interface ModalProviderProps {
  children: React.ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [modalTitle, setModalTitle] = useState<string | undefined>(undefined);
  const [modalType, setModalType] = useState<'simple' | 'confirm' | 'custom'>(
    'simple'
  );
  const [okBtnText, setOkBtnText] = useState('Подтвердить');
  const [cancelBtnText, setCancelBtnText] = useState('Отмена');
  const [opaqueBackground, setOpaqueBackground] = useState(false);
  const [modalClassName, setModalClassName] = useState('');
  const [containerWidth, setContainerWidth] = useState(416);
  const [size, setSize] = useState('md');
  const [sizeToContent, setSizeToContent] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: boolean) => void) | null
  >(null);
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(
    null
  );

  const [onConfirmCb, setOnConfirmCb] = useState<(() => void) | null>(null);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setModalContent(null);
    setModalTitle(undefined);
    setOpaqueBackground(false);
    setModalClassName('');
    setSizeToContent(false);
    setResolvePromise(null);
    setOnCloseCallback(null);
  }, []);

  const openModal = useCallback((config: ModalConfig) => {
    setModalType(config.modalType || 'simple');
    const requestedSize = config.size || 'md';
    const isAuto = requestedSize === 'auto';
    setSize(isAuto ? 'md' : requestedSize);
    setSizeToContent(isAuto);
    setModalContent(config.content);
    setModalTitle(config.title);
    setOpaqueBackground(config.opaqueBackground || false);
    setOnCloseCallback(() => config.onClose || null);
    setContainerWidth(config.containerWidth || 416);
    setIsOpen(true);
    setModalClassName(config.modalClassName || '');
  }, []);

  const openConfirmModal = useCallback(
    (config: ConfirmModalConfig): Promise<boolean> => {
      return new Promise((resolve) => {
        setModalType('confirm');
        setModalTitle(config.title);
        setModalContent(config.content);
        setOkBtnText(config.okBtn || 'Подтвердить');
        setCancelBtnText(config.cancelBtn || 'Отмена');
        setOpaqueBackground(config.opaqueBackground || false);
        setResolvePromise(() => resolve);
        setIsOpen(true);
        setModalClassName(config.modalClassName || '');
        setOnConfirmCb(() => config?.onConfirmCb);
      });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (resolvePromise) {
      onConfirmCb?.();
      resolvePromise(true);
    }
    closeModal();
  }, [resolvePromise, closeModal]);

  const handleCancel = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(false);
    }
    closeModal();
  }, [resolvePromise, closeModal]);

  const handleClose = useCallback(() => {
    if (modalType === 'confirm' && resolvePromise) {
      resolvePromise(false);
    }
    if (modalType === 'simple' && onCloseCallback) {
      onCloseCallback();
    }
    closeModal();
  }, [modalType, resolvePromise, onCloseCallback, closeModal]);

  return (
    <ModalContext.Provider value={{ openModal, openConfirmModal, closeModal }}>
      {children}

      {opaqueBackground ? (
        <div className="fixed inset-0 z-50 bg-gray-600 flex items-center justify-center">
          <div
            className="bg-white border border-gray-200 rounded-lg shadow-lg"
            style={{ width: `${containerWidth}px` }}
          >
            {(modalType === 'confirm' || modalTitle) && (
              <div className="flex items-center justify-between p-5 border-b border-gray-200">
                {modalTitle ? (
                  <h3 className="text-xl font-medium text-gray-900">
                    {modalTitle}
                  </h3>
                ) : (
                  <span />
                )}
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <HiX className="h-5 w-5" />
                </button>
              </div>
            )}

            <div className="p-5">
              <div className={modalType === 'confirm' ? 'space-y-6' : ''}>
                {typeof modalContent === 'string' ? (
                  <p className="text-normal leading-relaxed text-gray-500">
                    {modalContent}
                  </p>
                ) : (
                  modalContent
                )}
              </div>
            </div>

            {modalType === 'confirm' && (
              <div className="flex gap-4 items-center p-5 border-t border-gray-200">
                <Button color="primary" onClick={handleConfirm}>
                  {okBtnText}
                </Button>
                <Button color="second" onClick={handleCancel}>
                  {cancelBtnText}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <Modal
          show={isOpen}
          onClose={handleClose}
          dismissible={modalType !== 'confirm'}
          className={modalClassName}
          size={size}
          theme={
            sizeToContent
              ? createTheme({
                  root: {
                    sizes: {
                      md: 'w-max max-w-[90vw]',
                    },
                  },
                })
              : undefined
          }
        >
          {(modalType === 'confirm' || modalTitle) && (
            <ModalHeader>
              <div className="flex items-center justify-between w-full">
                {modalTitle ? (
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                    {modalTitle}
                  </h3>
                ) : (
                  <span />
                )}
              </div>
            </ModalHeader>
          )}

          {modalType === 'simple' && !modalTitle && (
            <div className="flex justify-end p-2">
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <HiX className="h-5 w-5" />
              </button>
            </div>
          )}

          <ModalBody className="overflow-visible">
            <div
              className={
                (modalType === 'confirm' ? 'space-y-6' : '') +
                (sizeToContent ? ' flex flex-col items-center w-full' : '')
              }
            >
              {typeof modalContent === 'string' ? (
                <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                  {modalContent}
                </p>
              ) : (
                modalContent
              )}
            </div>
          </ModalBody>

          {modalType === 'confirm' && (
            <ModalFooter>
              <Button color="primary" onClick={handleConfirm}>
                {okBtnText}
              </Button>
              <Button color="second" onClick={handleCancel}>
                {cancelBtnText}
              </Button>
            </ModalFooter>
          )}
        </Modal>
      )}
    </ModalContext.Provider>
  );
};
